// components
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import CreateButton from 'components/buttons/CreateButton';
import customerImg from 'assets/img/customer.png';
import LoadingCard from './components/loadingCard';
import Nft4 from 'assets/img/nfts/Nft4.png';
import SearchIcon from 'components/icons/SearchIcon';
import Card from 'components/card';
import Pagination from 'components/pagination/Pagination';
import { fetchFilter, setFilter } from 'utils/filtersStorage';
import SupplierCard from 'components/card/SupplierCard';
import { getPaginatedSuppliers } from 'interfaces/supplier';
import { MdViewModule, MdTableRows, MdArrowUpward, MdArrowDownward } from 'react-icons/md';

const Suppliers = () => {
    const nav = useNavigate();
    const [suppliers, setSuppliers] = useState(null);
    const [suppliersMeta, setSuppliersMeta] = useState(null);
    const [search, setSearch] = useState('');
    const [firstFetch, setFirstFetch] = useState(true);
    const [initSearch, setInitSearch] = useState(true);
    const [page, setPage] = useState(1);
    const [limitPerPage, setLimitPerPage] = useState(12);
    const isFullyLoaded = suppliers;
    const [view, setView] = useState<'grid' | 'table'>(fetchFilter('suppliers')?.view || 'grid');
    const [sortColumn, setSortColumn] = useState<'company' | 'contact' | 'mail' | 'phone' | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSort = (column: 'company' | 'contact' | 'mail' | 'phone') => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedSuppliers = useMemo(() => {
        if (!suppliers || !sortColumn) return suppliers || [];
        
        return [...suppliers].sort((a, b) => {
            let aValue: string, bValue: string;
            
            switch (sortColumn) {
                case 'company':
                    aValue = (a.company || a.displayName || '').toLowerCase();
                    bValue = (b.company || b.displayName || '').toLowerCase();
                    break;
                case 'contact':
                    aValue = (a.contact || '').toLowerCase();
                    bValue = (b.contact || '').toLowerCase();
                    break;
                case 'mail':
                    aValue = (a.mail || '').toLowerCase();
                    bValue = (b.mail || '').toLowerCase();
                    break;
                case 'phone':
                    aValue = (a.phone || '').toLowerCase();
                    bValue = (b.phone || '').toLowerCase();
                    break;
                default:
                    return 0;
            }
            
            if (sortDirection === 'asc') {
                return aValue.localeCompare(bValue);
            } else {
                return bValue.localeCompare(aValue);
            }
        });
    }, [suppliers, sortColumn, sortDirection]);

    const fetchSuppliers = async (page: number, limit: number) => {
        const res = await getPaginatedSuppliers(page, limit, search);
        if (res) {
            setSuppliers(res.data);
            setSuppliersMeta(res.meta);
            setPage(page);
            setLimitPerPage(limit);
            if (firstFetch) setFirstFetch(false);
        }
    };

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            nav('/login');
        }
        // First fetch handling cached filters
        const filters = fetchFilter('suppliers');
        if (filters) {
            setSearch(filters.search || '');
            setView(filters.view || 'grid');
        } else {
            fetchSuppliers(
                filters?.page || page,
                filters?.limit || limitPerPage,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!firstFetch)
            setFilter('suppliers', { page, limit: limitPerPage, search, view });
    }, [page, limitPerPage, search, view, firstFetch]);

    useEffect(() => {
        // If init (search = '') but cached search is not empty,return
        const filters = fetchFilter('suppliers');
        if (initSearch && filters?.search !== '') {
            setInitSearch(false);
            return;
        }

        // Handle the first fetch with cached filters if exists
        if (firstFetch) {
            const filters = fetchFilter('suppliers');
            if (filters) {
                fetchSuppliers(
                    filters?.page || page,
                    filters?.limit || limitPerPage,
                );
            }
        } else {
            // Handle normal search
            setSuppliers(null);
            const delayDebounceFn = setTimeout(() => {
                fetchSuppliers(1, limitPerPage);
            }, 800);
            return () => clearTimeout(delayDebounceFn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <>
            <Card extra="mt-2 pt-[10px] pl-[20px] pr-[20px] pb-[20px] border">
                <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
                    <div className="h-full w-full rounded-[20px]">
                        <div className="mt-3 flex justify-between items-center gap-3 flex-col sm:flex-row">
                            <div className="flex h-[38px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white w-full sm:w-[50%] lg:w-[40%]">
                                <SearchIcon />
                                <input
                                    type="text"
                                    placeholder={
                                        'Entreprise, mail, téléphone, contact, ....'
                                    }
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                />
                            </div>
                            <div className="flex gap-2">
                                <CreateButton
                                    value="Ajouter un fournisseur"
                                    extra="my-auto h-10 col-start-1 col-span-6 md:col-span-4 lg:col-start-8 lg:col-span-3 3xl:col-start-9 3xl:col-span-2"
                                    onClick={() => nav('/create-supplier')}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'grid' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                                    title="Vue cartes"
                                    onClick={() => setView('grid')}
                                >
                                    <MdViewModule />
                                </button>
                                <button
                                    className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'table' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                                    title="Vue tableau"
                                    onClick={() => setView('table')}
                                >
                                    <MdTableRows />
                                </button>
                            </div>
                        </div>
                        
                        {/* Vue: GRID */}
                        {view === 'grid' && (
                            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
                                {isFullyLoaded && suppliers.length === 0 && (
                                    <div className="col-span-12 mt-10">
                                        <img
                                            className="mx-auto flex h-[300px] w-[300px] items-center justify-center"
                                            src={customerImg}
                                            alt=""
                                        />
                                        <p className="col-span-12 my-auto mt-3 text-center text-base text-navy-700 dark:text-white">
                                            Gérer facilement vos fournisseurs et vos
                                            achats
                                        </p>
                                        <div className="font-large mt-5 flex items-center justify-center gap-5 rounded-xl">
                                            <button
                                                className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                                onClick={() =>
                                                    nav('/create-supplier')
                                                }
                                            >
                                                Ajouter votre premier fournisseur
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {isFullyLoaded
                                    ? suppliers.map(
                                          (supplier: any, index: number) => {
                                              return (
                                                  <div
                                                      key={supplier._id}
                                                      className={`step-customer-${index}`}
                                                  >
                                                      <SupplierCard
                                                          id={supplier._id}
                                                          company={supplier.company}
                                                          displayName={
                                                              supplier.displayName
                                                          }
                                                          email={supplier.mail}
                                                          phone={supplier.phone}
                                                          commands="0"
                                                          image={Nft4}
                                                          customerId={
                                                              supplier.customerId
                                                          }
                                                      />
                                                  </div>
                                              );
                                          },
                                      )
                                    : Array(8)
                                          .fill(0)
                                          .map((_, i) => <LoadingCard key={i} />)}
                            </div>
                        )}

                        {/* Vue: TABLE */}
                        {view === 'table' && (
                            <div className="mt-8 overflow-x-auto rounded-xl border border-gray-200">
                                <table className="min-w-full text-sm text-gray-700">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th 
                                                className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                                                onClick={() => handleSort('company')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Entreprise
                                                    {sortColumn === 'company' && (
                                                        sortDirection === 'asc' ? <MdArrowUpward className="text-xs" /> : <MdArrowDownward className="text-xs" />
                                                    )}
                                                </div>
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                                                onClick={() => handleSort('contact')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Contact
                                                    {sortColumn === 'contact' && (
                                                        sortDirection === 'asc' ? <MdArrowUpward className="text-xs" /> : <MdArrowDownward className="text-xs" />
                                                    )}
                                                </div>
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                                                onClick={() => handleSort('mail')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Email
                                                    {sortColumn === 'mail' && (
                                                        sortDirection === 'asc' ? <MdArrowUpward className="text-xs" /> : <MdArrowDownward className="text-xs" />
                                                    )}
                                                </div>
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                                                onClick={() => handleSort('phone')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Téléphone
                                                    {sortColumn === 'phone' && (
                                                        sortDirection === 'asc' ? <MdArrowUpward className="text-xs" /> : <MdArrowDownward className="text-xs" />
                                                    )}
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isFullyLoaded
                                            ? sortedSuppliers.map((s: any) => (
                                                <tr
                                                    key={s._id}
                                                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => nav(`/supplier/${s._id}`)}
                                                    title="Ouvrir la fiche fournisseur"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            nav(`/supplier/${s._id}`);
                                                        }
                                                    }}
                                                >
                                                    <td className="py-3 px-4 font-medium text-gray-800">{s.company || s.displayName}</td>
                                                    <td className="py-3 px-4">{s.contact || '-'}</td>
                                                    <td className="py-3 px-4">{s.mail || '-'}</td>
                                                    <td className="py-3 px-4">{s.phone || '-'}</td>
                                                </tr>
                                            ))
                                            : Array(8).fill(0).map((_, i) => (
                                                <tr key={i} className="border-t border-gray-100">
                                                    <td className="py-3 px-4" colSpan={4}>Chargement...</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                {/* pagination */}
                <Pagination
                    extra="mt-3"
                    maxItems={suppliersMeta ? suppliersMeta.total : 12}
                    defaultItemsPerPage={limitPerPage}
                    itemsPerPage={[12, 24, 48]}
                    page={page}
                    onChange={(page: number, limit: number) =>
                        fetchSuppliers(page, limit)
                    }
                />
            </Card>
        </>
    );
};

export default Suppliers;
